import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  Check,
  Clock3,
  Kanban,
  ListOrdered,
  PhoneCall,
  Search,
  ShieldCheck,
  ShieldOff,
  Mic,
  type LucideIcon,
} from 'lucide-react';
import { Badge, Button, Card, CardContent, formatMoney } from '@salesbee/ui';
import { trpc } from '../lib/trpc';
import { Wordmark } from '../shell/Logo';

// Public landing page at `/` (signed-in visitors go straight to /dashboard, see routes.tsx).

type Feature = { icon: LucideIcon; title: string; body: string; points: string[] };

const FEATURES: Feature[] = [
  {
    icon: Search,
    title: 'Prospecting',
    body: 'Find businesses and people by industry, location and keywords, then bring them into your CRM in one click.',
    points: [
      'Search by industry, city and keywords',
      'One-click import, duplicates skipped',
      'Monthly prospect allowance per plan',
    ],
  },
  {
    icon: PhoneCall,
    title: 'AI outbound calling',
    body: 'Run calling campaigns with an AI voice agent that introduces itself as AI, qualifies the lead and books the meeting.',
    points: [
      'Every call logged with outcome and summary',
      'Full transcript on each contact',
      'Monthly calling minutes per plan',
    ],
  },
  {
    icon: ListOrdered,
    title: 'Sequences',
    body: 'Multi-step call, email and SMS cadences that stop on their own when someone replies, books or opts out.',
    points: [
      'Call, email and SMS steps',
      'Stops on reply, booking or opt-out',
      'Every step passes the compliance gate',
    ],
  },
  {
    icon: Kanban,
    title: 'A lean CRM and pipeline',
    body: 'Contacts, companies and deals in one place, with a pipeline your team will actually keep up to date.',
    points: [
      'Deals tracked stage by stage',
      'Activity timeline on every contact',
      'Unlimited users on every plan',
    ],
  },
];

const COMPLIANCE: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: ShieldOff,
    title: 'Do-not-call list',
    body: 'Numbers on your internal list, and national registries where connected, are never dialled.',
  },
  {
    icon: Clock3,
    title: 'Calling-hours guard',
    body: "Calls only go out within allowed hours in the contact's own time zone.",
  },
  {
    icon: Mic,
    title: 'AI disclosure',
    body: 'The voice agent says it is an AI at the start of every call, with your business name and caller ID.',
  },
  {
    icon: ShieldCheck,
    title: 'Stricter, never looser',
    body: 'The legal floor is built in. You can tighten the rules for your team, never switch them off.',
  },
];

export function LandingPage() {
  const plans = trpc.plans.useQuery();
  const trialDays = plans.data?.trialDays;
  return (
    <div className="min-h-dvh bg-background">
      <header className="mx-auto flex h-16 max-w-[1100px] items-center justify-between px-4 sm:px-8">
        <Link to="/" aria-label="SalesBee home">
          <Wordmark />
        </Link>
        <nav className="flex items-center gap-1 sm:gap-2">
          <Button asChild variant="ghost" className="hidden sm:inline-flex">
            <Link to="/pricing">Pricing</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/sign-in">Sign in</Link>
          </Button>
          <Button asChild>
            <Link to="/sign-up">Start free trial</Link>
          </Button>
        </nav>
      </header>

      <main>
        <section className="mx-auto max-w-[1100px] px-4 pb-16 pt-14 text-center sm:px-8 sm:pt-20">
          <h1 className="mx-auto max-w-3xl text-[40px] font-semibold leading-[1.1] tracking-[-0.03em] sm:text-[52px]">
            Prospect, call and close, with compliance built in.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-[17px] text-muted-foreground sm:text-[19px]">
            SalesBee is a lean sales CRM with prospecting, AI outbound calling and multi-step sequences.
            Do-not-call lists, calling hours and AI disclosure are checked on every dial.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg">
              <Link to="/sign-up">
                Start {trialDays ? `${trialDays}-day ` : ''}free trial <ArrowRight />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/pricing">See pricing</Link>
            </Button>
          </div>
          <p className="mt-4 text-[13px] text-muted-foreground">
            Prices in Canadian dollars. Unlimited users on every plan.
          </p>
        </section>

        <section aria-labelledby="features" className="mx-auto max-w-[1100px] px-4 pb-16 sm:px-8">
          <h2 id="features" className="sr-only">
            Features
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {FEATURES.map((f) => (
              <Card key={f.title}>
                <CardContent className="space-y-3 py-6">
                  <div className="flex items-center gap-3">
                    <span className="flex size-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <f.icon className="size-5" />
                    </span>
                    <h3 className="text-[19px] font-semibold">{f.title}</h3>
                  </div>
                  <p className="text-[15px] text-muted-foreground">{f.body}</p>
                  <ul className="space-y-1.5 text-[15px]">
                    {f.points.map((p) => (
                      <li key={p} className="flex gap-2">
                        <Check className="mt-0.5 size-4 shrink-0 text-success" /> {p}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section aria-labelledby="compliance" className="border-y border-border bg-muted/40">
          <div className="mx-auto max-w-[1100px] px-4 py-16 sm:px-8">
            <h2 id="compliance" className="text-center text-[30px] font-semibold tracking-[-0.02em]">
              Compliance on every call
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-[16px] text-muted-foreground">
              Outbound calling only works if it stays on the right side of the rules. SalesBee checks them
              before every dial, call, email and text.
            </p>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {COMPLIANCE.map((c) => (
                <div key={c.title} className="space-y-2">
                  <c.icon className="size-6 text-primary" />
                  <h3 className="text-[16px] font-semibold">{c.title}</h3>
                  <p className="text-[14px] text-muted-foreground">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section aria-labelledby="plans" className="mx-auto max-w-[1100px] px-4 py-16 sm:px-8">
          <h2 id="plans" className="text-center text-[30px] font-semibold tracking-[-0.02em]">
            Simple monthly plans
          </h2>
          <div className="mx-auto mt-10 grid max-w-3xl gap-6 md:grid-cols-2">
            {plans.data?.plans.map((p) => (
              <Card key={p.id} className={p.badge ? 'ring-2 ring-primary' : ''}>
                <CardContent className="space-y-4 py-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[18px] font-semibold">{p.name}</h3>
                    {p.badge ? <Badge variant="primary">{p.badge}</Badge> : null}
                  </div>
                  <p className="font-mono text-[30px] font-semibold">
                    {formatMoney(p.priceCents)}{' '}
                    <span className="text-[14px] font-normal text-muted-foreground">CAD / month</span>
                  </p>
                  <ul className="space-y-1.5 text-[14px]">
                    {p.highlights.slice(0, 4).map((h) => (
                      <li key={h} className="flex gap-2">
                        <Check className="mt-0.5 size-4 shrink-0 text-success" /> {h}
                      </li>
                    ))}
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/sign-up" search={{ plan: p.id }}>
                      Start free trial
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-6 text-center">
            <Link to="/pricing" className="text-[15px] text-primary hover:underline">
              Compare plans in detail
            </Link>
          </p>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-[1100px] flex-col items-center justify-between gap-3 px-4 py-8 text-[13px] text-muted-foreground sm:flex-row sm:px-8">
          <span>SalesBee by WRKRBZ</span>
          <nav className="flex gap-4">
            <Link to="/pricing" className="hover:text-foreground">
              Pricing
            </Link>
            <Link to="/sign-in" className="hover:text-foreground">
              Sign in
            </Link>
            <Link to="/sign-up" className="hover:text-foreground">
              Start free trial
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
